import { Elements } from '@stripe/react-stripe-js';
import type { Stripe } from '@stripe/stripe-js';
import type { PropsWithChildren } from 'react';

import colors from '@wecasa/common/src/utils/colors';
import { getCurrentCurrency } from '@wecasa/common/src/utils/country';
import { getStripeLocale } from '@wecasa/common/src/utils/stripe';
import { toRem } from '@wecasa/common/src/utils/styles';

const PaymentProvider = ({
  children,
  stripePromise,
}: PropsWithChildren<{ stripePromise: Promise<Stripe | null> | null }>) => (
  <Elements
    stripe={stripePromise}
    options={{
      locale: getStripeLocale(),
      // 'mode' and 'currency' should be defined otherwise it crashes
      // 'mode' will be updated in payment-section component with the correct value
      // Currency will be optional in stripe-js 8.1.0
      // @see https://github.com/stripe/stripe-js/releases/tag/v8.1.0
      mode: 'setup',
      currency: getCurrentCurrency().toLowerCase(),
      fonts: [
        {
          cssSrc: 'https://fonts.googleapis.com/css2?family=Work+Sans:wght@400;500;600;700;800;900&display=swap',
        },
      ],
      appearance: {
        theme: 'flat',
        variables: {
          fontFamily: "'Work Sans', sans-serif",
          fontSizeXs: toRem(16),
          fontSizeSm: toRem(13),
          borderRadius: '8px',
          colorPrimary: colors.neutral100,
          colorTextSecondary: colors.neutral100,
          colorText: colors.neutral100,
          colorDanger: colors.error100,
        },
        rules: {
          '.AccordionItem': {
            padding: '17px',
          },
          '.AccordionItem--selected': {
            borderColor: colors.orange100,
            borderWidth: '3px',
            borderStyle: 'solid',
          },
          '.PickerItem': {
            backgroundColor: colors.white100,
            borderColor: colors.neutral20,
            borderWidth: '1px',
            borderStyle: 'solid',
          },
          '.PickerItem--highlight': {
            borderColor: colors.orange100,
            borderWidth: '3px',
            boxShadow: 'none',
            borderStyle: 'solid',
          },
          '.PickerItem--highlight:hover': {
            boxShadow: 'none',
          },
          '.Input': {
            backgroundColor: colors.neutral10,
          },
          '.Input:focus': {
            backgroundColor: colors.white100,
            boxShadow: 'none',
            borderWidth: '3px',
            borderStyle: 'solid',
            borderColor: colors.orange100,
          },
        },
      },
    }}
  >
    {children}
  </Elements>
);

export default PaymentProvider;
